import { useCallback, useEffect, useRef, useState } from "react";

interface UseCopyToClipboardOptions {
  /** How long `copied` stays true after a successful copy. */
  resetMs?: number;
  /** Called when the copy fails or the Clipboard API is unavailable. */
  onError?: () => void;
}

/**
 * Copy text to the clipboard with transient `copied` feedback.
 *
 * navigator.clipboard is undefined in non-secure contexts and legacy
 * WebViews, and writeText rejects on denied permission / no document focus —
 * both paths report through `onError` so callers never fail silently.
 */
export function useCopyToClipboard({ resetMs = 1500, onError }: UseCopyToClipboardOptions = {}) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Callers pass inline onError lambdas; reading through a ref keeps `copy`
  // referentially stable instead of recreating it every render.
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  });

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const reset = useCallback(() => {
    clearTimeout(resetTimer.current);
    setCopied(false);
  }, []);

  const copy = useCallback(
    (text: string) => {
      if (!navigator.clipboard) {
        onErrorRef.current?.();
        return;
      }
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          clearTimeout(resetTimer.current);
          resetTimer.current = setTimeout(() => setCopied(false), resetMs);
        })
        .catch(() => onErrorRef.current?.());
    },
    [resetMs],
  );

  return { copied, copy, reset };
}
